import { AppCategory } from './app.model';
import { Common } from './common.model';
import { Embedded, EmbeddedApp, EmbeddedUser, toAppEmbedded, toUserEmbedded } from './embed.model';

export type embed = keyof EmbedProperties;

export interface EmbedProperties {
    $createdBy: EmbeddedUser;
    $updatedBy: EmbeddedUser;
    $app: {id: string; name: string; logo: string; category: AppCategory};
}

export type EmbedReport<T extends embed> = Report & Partial<Pick<EmbedProperties, T>>;

export const configuration: {[K in embed]: {embed: string; transform: (project: Partial<Report>, embedded: Embedded) => EmbedProperties[K] }} = {
    $createdBy: {
        embed: 'users',
        transform: (data: Report, embedded: Embedded): EmbeddedUser => toUserEmbedded(data, 'createdBy', embedded),
    },
    $updatedBy: {
        embed: 'users',
        transform: (data: Report, embedded: Embedded): EmbeddedUser => toUserEmbedded(data, 'updatedBy', embedded),
    },
    $app: {
        embed: 'apps',
        transform: (data: Report, embedded: Embedded): EmbeddedApp => toAppEmbedded(data, 'app', embedded),
    },
};

export class Report extends Common {
    public app: string;
    public name: string;
    public category: string;
    public internal: boolean;
    public requiresRole: boolean;
    public authorizedRoles: string[];
    public configPowerBi: PowerBiConfig;
    public parameters: Parameter[];

    constructor(data: any = {}) {
        super(data);
        this.app = data.app;
        this.name = data.name;
        this.category = data.category;
        this.internal = data.internal;
        this.requiresRole = data.requiresRole;
        this.authorizedRoles = data.authorizedRoles;
        this.configPowerBi = new PowerBiConfig(data.configPowerBi);
        this.parameters = data.parameters ?? [];
    }

    public static toEmbeddedReport<T extends embed>(data: Partial<Report>, embedded: Embedded, customFields: T[]): EmbedReport<T> {
        const customData = customFields.reduce((acc, field: embed) => {
            acc[field] = configuration[field].transform(data, embedded);
            return acc;
        }, {});
        return {...new Report(data), ...customData} as EmbedReport<T>;
    }
}

export class PowerBiConfig {
    public reportId: string;
    public workspaceId: string;
    public rlsEnabled: boolean;
    public datasetId: string;
    public serviceUser: string;
    public secretPath: string;
    public rlsRole: string;

    constructor(data: any = {}) {
        this.reportId = data.reportId;
        this.workspaceId = data.workspaceId;
        this.rlsEnabled = data.rlsEnabled;
        this.datasetId = data.datasetId;
        this.serviceUser = data.serviceUser;
        this.secretPath = data.secretPath;
        this.rlsRole = data.rlsRole;
    }
}


export class Parameter {
    public name: string;
    public multi: boolean;
    public values: string[];
    public projectMetadataName: string;
    public defaultValue: string;

    constructor(data: any = {}) {
        this.name = data.name;
        this.multi = data.multi;
        this.values = data.values;
        this.projectMetadataName = data.projectMetadataName;
        this.defaultValue = data.defaultValue;
    }
}
